import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines } from "../components/Reveal";

const BG = {
  corporate: { src: "/images/bg-corporate-halftone.png", pos: "center", opacity: "opacity-[0.07] group-hover:opacity-[0.11]" },
  creative: { src: "/images/bg-creative-halftone.png", pos: "center 40%", opacity: "opacity-[0.06] group-hover:opacity-[0.1]" },
};

function Side({ route, hovered, setHovered, glow, children, testId, ariaLabel }) {
  const active = hovered === route;
  const dimmed = hovered && hovered !== route;
  const bg = BG[route];
  const torchRef = useRef(null);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    torchRef.current?.style.setProperty("--tx", `${e.clientX - r.left}px`);
    torchRef.current?.style.setProperty("--ty", `${e.clientY - r.top}px`);
  };

  return (
    <div
      className="group relative min-h-[50svh] overflow-hidden bg-[#0B0C0E] md:min-h-0 md:h-[100svh]"
      style={{
        flexGrow: active ? 1.6 : dimmed ? 0.62 : 1,
        flexBasis: 0,
        transition: "flex-grow 0.9s cubic-bezier(0.22, 1, 0.36, 1)",
      }}
      onMouseEnter={() => setHovered(route)}
      onMouseLeave={() => setHovered(null)}
      onMouseMove={onMove}
      data-testid={testId}
    >
      <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ ...glow, scale: 1.08 }}>
        <div
          data-testid={`${testId}-texture`}
          className={`absolute inset-0 bg-no-repeat blur-[1.5px] transition-opacity duration-700 ${bg.opacity}`}
          style={{ backgroundImage: `url(${bg.src})`, backgroundSize: "cover", backgroundPosition: bg.pos }}
        />
        <div
          ref={torchRef}
          data-testid={`${testId}-torch`}
          className="absolute inset-0 bg-no-repeat opacity-0 transition-opacity duration-500 group-hover:opacity-[0.55]"
          style={{
            backgroundImage: `url(${bg.src})`,
            backgroundSize: "cover",
            backgroundPosition: bg.pos,
            "--tx": "50%",
            "--ty": "50%",
            WebkitMaskImage: "radial-gradient(circle 260px at var(--tx) var(--ty), rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 35%, rgba(0,0,0,0) 72%)",
            maskImage: "radial-gradient(circle 260px at var(--tx) var(--ty), rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 35%, rgba(0,0,0,0) 72%)",
          }}
        />
        <div
          className={`absolute h-[70vmin] w-[70vmin] rounded-full blur-[120px] opacity-0 transition-opacity duration-700 group-hover:opacity-100 ${
            route === "corporate" ? "-left-[10vmin] top-1/2 bg-white/[0.05]" : "-right-[10vmin] top-1/2 bg-umber/[0.09]"
          }`}
        />
      </motion.div>
      <div className="noise pointer-events-none absolute inset-0 opacity-60" />
      <Link
        to={`/${route}`}
        aria-label={ariaLabel}
        data-testid={`${testId}-enter-link`}
        className="relative flex h-full flex-col justify-between p-6 pb-10 pt-24 lg:p-12 lg:pt-28"
      >
        {children}
      </Link>
    </div>
  );
}

export default function Landing() {
  const [hovered, setHovered] = useState(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const px = useTransform(sx, [-1, 1], [-18, 18]);
  const py = useTransform(sy, [-1, 1], [-12, 12]);

  useEffect(() => {
    document.title = "Haus Ov Harii · Design, Illustration & Art Direction";
  }, []);

  const onMove = (e) => {
    mx.set((e.clientX / window.innerWidth) * 2 - 1);
    my.set((e.clientY / window.innerHeight) * 2 - 1);
  };

  return (
    <section
      className="relative flex flex-col md:h-[100svh] md:flex-row"
      onMouseMove={onMove}
      data-testid="landing-splitscreen"
    >
      <div className="pointer-events-none absolute left-1/2 top-16 z-10 hidden -translate-x-1/2 md:block">
        <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/50 mix-blend-difference">
          Est. 05/2016 · Worldwide
        </span>
      </div>

      <Side
        route="corporate"
        hovered={hovered}
        setHovered={setHovered}
        glow={{ x: px, y: py }}
        testId="landing-corporate-side"
        ariaLabel="Enter the Corporate practice"
      >
        <p className="eyebrow">01 / Brand &amp; Campaign at Scale</p>
        <div>
          <h1 className="text-4xl font-medium tracking-tighter sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["CORPORATE"]} delay={0.25} />
          </h1>
          <MaskedLines
            lines={["Brand systems and campaigns for", "institutions at global scale."]}
            delay={0.55}
            className="mt-5 max-w-md text-base font-light leading-relaxed text-[#9EA3B0]"
          />
          <div className="mt-8 flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#616775]">
            {["Identity", "Campaign", "Communications"].map((t) => (
              <span key={t} className="border border-white/15 px-3 py-1.5">{t}</span>
            ))}
          </div>
          <span className="mt-10 inline-flex items-center gap-2 border-b border-white/20 pb-1 font-mono text-[11px] uppercase tracking-[0.3em] text-white/80 transition-colors duration-300 group-hover:border-umber group-hover:text-umber">
            Enter Corporate <ArrowUpRight size={14} />
          </span>
        </div>
      </Side>

      <div className="relative z-10 h-px w-full bg-white/15 md:h-auto md:w-px" aria-hidden="true" />

      <Side
        route="creative"
        hovered={hovered}
        setHovered={setHovered}
        glow={{ x: px, y: py }}
        testId="landing-creative-side"
        ariaLabel="Enter the Creative practice"
      >
        <p className="eyebrow">02 / Art Direction &amp; Image</p>
        <div>
          <h1 className="text-4xl font-medium tracking-tighter sm:text-5xl lg:text-6xl">
            <span className="block overflow-hidden pb-[0.08em]">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                CREA
                <span className="inline-block -skew-x-12 font-serif italic text-umber transition-transform duration-500 group-hover:skew-x-0">
                  T
                </span>
                IVE
              </motion.span>
            </span>
          </h1>
          <MaskedLines
            lines={["Illustration, identity and image for", "culture, music and hospitality."]}
            delay={0.65}
            className="mt-5 max-w-md text-base font-light leading-relaxed text-[#9EA3B0]"
          />
          <div className="mt-8 flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#616775]">
            {["Illustration", "Packaging", "Motion"].map((t) => (
              <span key={t} className="border border-white/15 px-3 py-1.5">{t}</span>
            ))}
          </div>
          <span className="mt-10 inline-flex items-center gap-2 border-b border-white/20 pb-1 font-mono text-[11px] uppercase tracking-[0.3em] text-white/80 transition-colors duration-300 group-hover:border-umber group-hover:text-umber">
            Enter Creative <ArrowUpRight size={14} />
          </span>
        </div>
      </Side>
    </section>
  );
}
